import { useState } from 'react'
import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link, data } from 'react-router'
import { db } from '@afficheservices/db'
import {
  professionalProfiles,
  reviews as reviewsTable,
  reviewResponses,
  users,
  professionalCategories,
  serviceCategories,
  professionalCertifications,
  professionalPortfolioImages,
  professionalServiceAreas,
  reviewImages,
  leads,
  contractTemplates,
} from '@afficheservices/db/schema'
import { eq, and, desc, sql, isNull, gte } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import {
  Star, CheckCircle, Shield, Clock, MapPin, Briefcase,
  Users as UsersIcon, ArrowRight, Award, Globe, Building2,
  Calendar, ThumbsUp, MessageSquare, Image as ImageIcon,
  ChevronLeft, ChevronRight, X, BadgeCheck, Sparkles,
  Phone, ExternalLink, FileCheck,
} from 'lucide-react'
import { GuaranteeBadge } from '~/components/trust-guarantee'
import { LocalBusinessSchema, ReviewSchema, FAQSchema, BreadcrumbSchema, geoMeta } from '~/components/structured-data'
import { PROFILE_PAGE_HEADERS, DYNAMIC_PAGE_HEADERS } from '~/lib/cache.server'
import { formatRating, formatHours } from '~/lib/format'
import { InlineQuiz } from '~/components/inline-quiz'
import { ProDemoFunnel } from '~/components/pro-demo-funnel'
import { artisanSlug, SERVICE_LABELS } from '~/lib/programmatic-seo'

// Dynamic cache: short TTL for recently-updated pros (post-claim), so the
// CDN flips from ProDemoFunnel → standard profile within seconds, not 10min.
export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
  return loaderHeaders.has('Cache-Control') ? loaderHeaders : new Headers(PROFILE_PAGE_HEADERS)
}

// ─── Loader ──────────────────────────────────────────────────

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params

  const [pro] = await db
    .select()
    .from(professionalProfiles)
    .where(eq(professionalProfiles.slug, slug ?? ''))
    .limit(1)

  if (!pro) throw new Response('Professionnel non trouvé', { status: 404 })

  // Parallel data fetching
  const [
    [user],
    proCats,
    proReviews,
    certs,
    portfolio,
    serviceAreas,
    ratingBreakdown,
  ] = await Promise.all([
    // User info
    db.select({ firstName: users.firstName, lastName: users.lastName })
      .from(users).where(and(eq(users.id, pro.userId), isNull(users.deletedAt))).limit(1),

    // Categories — id needed for /demande-devis/1?cat=$id deep link
    db.select({
      id: serviceCategories.id,
      name: serviceCategories.nameFr,
      slug: serviceCategories.slug,
    })
      .from(professionalCategories)
      .innerJoin(serviceCategories, eq(serviceCategories.id, professionalCategories.categoryId))
      .where(eq(professionalCategories.professionalId, pro.id)),

    // Reviews with responses
    db.select({
      id: reviewsTable.id,
      overallRating: reviewsTable.overallRating,
      qualityRating: reviewsTable.qualityRating,
      punctualityRating: reviewsTable.punctualityRating,
      communicationRating: reviewsTable.communicationRating,
      valueRating: reviewsTable.valueRating,
      title: reviewsTable.title,
      comment: reviewsTable.comment,
      isVerifiedPurchase: reviewsTable.isVerifiedPurchase,
      createdAt: reviewsTable.createdAt,
      reviewerFirstName: users.firstName,
      reviewerLastName: users.lastName,
    })
      .from(reviewsTable)
      .innerJoin(users, eq(users.id, reviewsTable.reviewerId))
      .where(and(eq(reviewsTable.professionalId, pro.id), eq(reviewsTable.status, 'approved')))
      .orderBy(desc(reviewsTable.createdAt))
      .limit(20),

    // Certifications (audit #11 — was unbounded; cap at a sane upper bound
    // so a pro with 1000 certs can't blow up the loader).
    db.select()
      .from(professionalCertifications)
      .where(eq(professionalCertifications.professionalId, pro.id))
      .orderBy(professionalCertifications.createdAt)
      .limit(50),

    // Portfolio images — visible gallery shows ~20, fetch a small buffer.
    db.select()
      .from(professionalPortfolioImages)
      .where(eq(professionalPortfolioImages.professionalId, pro.id))
      .orderBy(professionalPortfolioImages.displayOrder)
      .limit(50),

    // Service areas
    db.select()
      .from(professionalServiceAreas)
      .where(eq(professionalServiceAreas.professionalId, pro.id))
      .limit(100),

    // Rating breakdown
    db.select({
      avgQuality: sql<string>`ROUND(AVG(${reviewsTable.qualityRating})::numeric, 1)`,
      avgPunctuality: sql<string>`ROUND(AVG(${reviewsTable.punctualityRating})::numeric, 1)`,
      avgCommunication: sql<string>`ROUND(AVG(${reviewsTable.communicationRating})::numeric, 1)`,
      avgValue: sql<string>`ROUND(AVG(${reviewsTable.valueRating})::numeric, 1)`,
      count5: sql<number>`count(*) FILTER (WHERE ${reviewsTable.overallRating} = 5)`,
      count4: sql<number>`count(*) FILTER (WHERE ${reviewsTable.overallRating} = 4)`,
      count3: sql<number>`count(*) FILTER (WHERE ${reviewsTable.overallRating} = 3)`,
      count2: sql<number>`count(*) FILTER (WHERE ${reviewsTable.overallRating} = 2)`,
      count1: sql<number>`count(*) FILTER (WHERE ${reviewsTable.overallRating} = 1)`,
    })
      .from(reviewsTable)
      .where(and(eq(reviewsTable.professionalId, pro.id), eq(reviewsTable.status, 'approved'))),
  ])

  // Fetch review responses + review photos
  const reviewIds = proReviews.map((r) => r.id)
  let responses: Record<string, string> = {}
  const reviewPhotos: Record<string, Array<{ id: string; imageUrl: string; caption: string | null }>> = {}

  if (reviewIds.length > 0) {
    const [resps, photos] = await Promise.all([
      db
        .select({
          reviewId: reviewResponses.reviewId,
          responseText: reviewResponses.responseText,
        })
        .from(reviewResponses)
        .where(
          and(
            eq(reviewResponses.professionalId, pro.id),
            eq(reviewResponses.status, 'approved'),
            sql`${reviewResponses.reviewId} IN ${reviewIds}`,
          ),
        ),
      db
        .select({
          id: reviewImages.id,
          reviewId: reviewImages.reviewId,
          imageUrl: reviewImages.imageUrl,
          caption: reviewImages.caption,
        })
        .from(reviewImages)
        .where(sql`${reviewImages.reviewId} IN ${reviewIds}`)
        .orderBy(reviewImages.displayOrder),
    ])

    responses = Object.fromEntries(resps.map((r) => [r.reviewId, r.responseText]))

    // Group photos by reviewId
    for (const photo of photos) {
      if (!reviewPhotos[photo.reviewId]) reviewPhotos[photo.reviewId] = []
      reviewPhotos[photo.reviewId].push({ id: photo.id, imageUrl: photo.imageUrl, caption: photo.caption })
    }
  }

  // Similar artisans — same primary category + same department,
  // excluding current pro. Limit 3 for the "Compare 3 devis" block.
  const primaryCatId = proCats[0]?.id
  let similarPros: Array<{ id: string; companyName: string; slug: string; city: string; ratingAvg: string | null; reviewCount: number; identityVerified: boolean; isDemo: boolean }> = []
  if (primaryCatId) {
    similarPros = await db
      .select({
        id: professionalProfiles.id,
        companyName: professionalProfiles.companyName,
        slug: professionalProfiles.slug,
        city: professionalProfiles.city,
        ratingAvg: professionalProfiles.ratingAvg,
        reviewCount: professionalProfiles.reviewCount,
        identityVerified: professionalProfiles.identityVerified,
        isDemo: professionalProfiles.isDemo,
      })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(
        eq(professionalCategories.categoryId, primaryCatId),
        eq(professionalProfiles.departmentCode, pro.departmentCode),
        sql`${professionalProfiles.id} != ${pro.id}`,
      ))
      .orderBy(desc(professionalProfiles.ratingAvg), desc(professionalProfiles.reviewCount))
      .limit(3)
  }

  // Live activity — count approved leads in this category + department
  // over the last 7 days. Used for urgency ticker under hero.
  let weeklyLeadCount = 0
  if (primaryCatId) {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    const [row] = await db
      .select({ count: sql<number>`count(*)::int` })
      .from(leads)
      .where(and(
        eq(leads.categoryId, primaryCatId),
        eq(leads.departmentCode, pro.departmentCode),
        gte(leads.createdAt, sevenDaysAgo),
      ))
    weeklyLeadCount = Number(row?.count ?? 0)
  }

  // Maintenance contract templates — surfaced as "Contrats d'entretien"
  // block on the profile. Only active templates shown to clients.
  const contracts = await db
    .select({
      id: contractTemplates.id,
      title: contractTemplates.title,
      description: contractTemplates.description,
      priceCents: contractTemplates.priceCents,
      interval: contractTemplates.interval,
    })
    .from(contractTemplates)
    .where(and(
      eq(contractTemplates.professionalId, pro.id),
      eq(contractTemplates.isActive, 1),
    ))
    .orderBy(contractTemplates.createdAt)
    .limit(10)

  const payload = {
    pro: {
      ...pro,
      ownerName: user ? `${user.firstName} ${user.lastName}` : '',
      categories: proCats,
    },
    contracts,
    reviews: proReviews.map((r) => ({
      ...r,
      response: responses[r.id] ?? null,
      photos: reviewPhotos[r.id] ?? [],
    })),
    certifications: certs,
    portfolio,
    serviceAreas,
    ratingBreakdown: ratingBreakdown[0] ?? null,
    similarPros,
    weeklyLeadCount,
  }

  // Recently updated → fresh cache so post-claim layout flip is immediate.
  // 1h grace covers Cloudflare's typical edge cache fan-out.
  const updatedRecently = pro.updatedAt && (Date.now() - new Date(pro.updatedAt).getTime() < 3600_000)
  const headers = updatedRecently ? DYNAMIC_PAGE_HEADERS : PROFILE_PAGE_HEADERS
  return data(payload, { headers })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const name = data?.pro?.companyName ?? 'Professionnel'
  const city = data?.pro?.city ?? ''
  const rating = data?.pro?.ratingAvg ?? '0'
  const count = data?.pro?.reviewCount ?? 0
  const slug = data?.pro?.slug ?? ''
  const desc = count > 0
    ? `${name} à ${city}. Note ${rating}/5 (${count} avis). Portfolio, certifications, zone d'intervention. Devis gratuit.`
    : `${name} à ${city}. Portfolio, certifications, zone d'intervention. Devis gratuit.`
  const url = `https://www.afficheservices.fr/pro/${slug}`
  const tags: ReturnType<MetaFunction> = [
    { title: `${name} — ${city} | AfficheServices` },
    { name: 'description', content: desc },
    { tagName: 'link', rel: 'canonical', href: url },
  ]
  // Noindex thin profiles. Two gates layered:
  //  1. isDemo (SIRENE-imported, unclaimed) + 0 reviews → noindex. SIRENE auto-
  //     populates description ≥200 chars on ~110k rows with near-identical
  //     boilerplate, so description length is NOT a uniqueness signal for
  //     demo pros. Real signal = a review or a claim (claim flips isDemo).
  //  2. Claimed pro (isDemo=false) + 0 reviews + no description → noindex.
  //     Once they earn a review OR fill description, page flips indexable.
  // Sitemap section uses the matching gate (isDemo=false + description≥200).
  const proDesc = data?.pro?.description ?? null
  const hasDescription = !!proDesc && proDesc.length >= 200
  const isDemo = data?.pro?.isDemo ?? false
  const shouldNoindex = isDemo
    ? count === 0
    : (count === 0 && !hasDescription)
  if (shouldNoindex) tags.push({ name: 'robots', content: 'noindex, follow' })
  return [
    ...tags,
    { property: 'og:title', content: `${name} — ${city}` },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { property: 'og:type', content: 'profile' },
    { property: 'og:site_name', content: 'AfficheServices' },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: `${name} — ${city}` },
    { name: 'twitter:description', content: desc },
    { property: 'og:image', content: `https://www.afficheservices.fr/api/og?pro=${encodeURIComponent(slug)}` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:locale', content: 'fr_FR' },
    ...geoMeta({
      region: data?.pro?.departmentCode ?? '',
      placename: city,
      lat: data?.pro?.latitude ? parseFloat(String(data.pro.latitude)) : null,
      lon: data?.pro?.longitude ? parseFloat(String(data.pro.longitude)) : null,
    }),
    { name: 'twitter:image', content: `https://www.afficheservices.fr/api/og?pro=${encodeURIComponent(slug)}` },
  ]
}

// ─── Sub-components ──────────────────────────────────────────

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}
        />
      ))}
    </div>
  )
}

function RatingBar({ label, value, max = 5 }: { label: string; value: number | null; max?: number }) {
  const pct = value ? (value / max) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs text-slate-500">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-amber-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-xs font-semibold text-slate-700">
        {value ?? '—'}
      </span>
    </div>
  )
}

function DistributionBar({ stars, count, total }: { stars: number; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0
  return (
    <div className="flex items-center gap-2">
      <span className="w-4 text-right text-xs text-slate-500">{stars}</span>
      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-6 text-right text-[11px] text-slate-400">{count}</span>
    </div>
  )
}

// ─── Gallery component ───────────────────────────────────────

function PortfolioGallery({ images }: { images: Array<{ id: string; imageUrl: string; caption: string | null }> }) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null)

  if (images.length === 0) return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
      <ImageIcon className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-500">Portfolio à venir</p>
      <p className="mt-1 text-xs text-slate-400">Ce professionnel n'a pas encore ajouté de photos de ses réalisations</p>
    </div>
  )

  return (
    <>
      <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ImageIcon className="h-5 w-5 text-slate-400" />
            Portfolio ({images.length})
          </h2>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setLightboxIdx(idx)}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-100"
            >
              <img
                src={img.imageUrl}
                alt={img.caption ?? `Photo ${idx + 1}`}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              {img.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                  <p className="text-xs text-white">{img.caption}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <button
            type="button"
            onClick={() => setLightboxIdx(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </button>

          {lightboxIdx > 0 && (
            <button
              type="button"
              onClick={() => setLightboxIdx(lightboxIdx - 1)}
              className="absolute left-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          <div className="max-h-[85vh] max-w-4xl">
            <img
              src={images[lightboxIdx].imageUrl}
              alt={images[lightboxIdx].caption ?? ''}
              className="max-h-[85vh] rounded-lg object-contain"
            />
            {images[lightboxIdx].caption && (
              <p className="mt-3 text-center text-sm text-white/80">{images[lightboxIdx].caption}</p>
            )}
            <p className="mt-1 text-center text-xs text-white/50">
              {lightboxIdx + 1} / {images.length}
            </p>
          </div>

          {lightboxIdx < images.length - 1 && (
            <button
              type="button"
              onClick={() => setLightboxIdx(lightboxIdx + 1)}
              className="absolute right-4 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </div>
      )}
    </>
  )
}

// ─── Main page ───────────────────────────────────────────────

interface ContractCard {
  id: string
  title: string
  description: string | null
  priceCents: number
  interval: 'monthly' | 'quarterly' | 'biannual' | 'annual'
}

function ContractsBlock({ contracts, proCompany }: { contracts: ContractCard[]; proCompany: string }) {
  const intervalLabels: Record<string, string> = {
    monthly: 'par mois',
    quarterly: 'par trimestre',
    biannual: 'par semestre',
    annual: 'par an',
  }
  async function handleSubscribe(templateId: string) {
    try {
      const res = await fetch('/api/contracts/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId }),
      })
      if (res.status === 401) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`
        return
      }
      const data = await res.json() as { url?: string; error?: string }
      if (data.url) window.location.href = data.url
      else alert(`Erreur : ${data.error ?? 'subscription_failed'}`)
    } catch {
      alert('Erreur réseau, réessayez')
    }
  }

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
      <h2 className="text-lg font-bold text-emerald-900">Contrats d'entretien</h2>
      <p className="mt-1 text-xs text-emerald-700">Souscrivez à un entretien régulier avec {proCompany}.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {contracts.map((c) => (
          <div key={c.id} className="rounded-xl border border-emerald-100 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">{c.title}</h3>
            {c.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{c.description}</p>}
            <div className="mt-3 flex items-baseline gap-1 text-emerald-800">
              <span className="text-xl font-bold">{(c.priceCents / 100).toFixed(0)} €</span>
              <span className="text-xs text-emerald-700">{intervalLabels[c.interval]}</span>
            </div>
            <button
              type="button"
              onClick={() => handleSubscribe(c.id)}
              className="mt-3 w-full rounded-xl bg-emerald-700 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-800"
            >
              S'abonner
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ProProfile() {
  const { pro, reviews, certifications, portfolio, serviceAreas, ratingBreakdown, similarPros, weeklyLeadCount, contracts } = useLoaderData<typeof loader>()

  // Unclaimed SIRENE imports → quote-request funnel layout.
  // Sidesteps the full profile UI; surfaces claimed competitors instead.
  if (pro.isDemo) {
    return (
      <ProDemoFunnel
        pro={{
          id: pro.id,
          slug: pro.slug,
          companyName: pro.companyName,
          siret: pro.siret,
          city: pro.city,
          postalCode: pro.postalCode,
          departmentCode: pro.departmentCode,
          addressLine1: pro.addressLine1,
          addressLine2: pro.addressLine2,
          categories: pro.categories,
        }}
        similarPros={similarPros}
        weeklyLeadCount={weeklyLeadCount}
      />
    )
  }
  const [reviewFilter, setReviewFilter] = useState<number | null>(null)

  const totalReviews = pro.reviewCount ?? 0
  const ratingNum = parseFloat(pro.ratingAvg ?? '0')
  const responseHours = parseFloat(pro.responseTimeAvgHours ?? '0')
  // parseFloat can yield NaN for malformed decimal data; coerce to 0 so the
  // UI never renders "NaN%" (audit #21). Round to integer for display.
  const acceptanceRateRaw = parseFloat(pro.leadAcceptanceRate ?? '0')
  const acceptanceRate = Number.isFinite(acceptanceRateRaw) ? Math.round(acceptanceRateRaw) : 0

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      {/* Directory listing banner — imported from SIRENE, not yet claimed */}
      {pro.isDemo && (
        <div className="border-b border-blue-200 bg-blue-50">
          <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-2.5 lg:px-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <p className="text-xs text-blue-800">
                <span className="font-semibold">Fiche annuaire</span> — Données issues du registre SIRENE (INSEE). Ce professionnel n'a pas encore revendiqué son profil.
              </p>
            </div>
            <Link to={`/revendiquer?siret=${pro.siret}`} className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-hover">
              Vous êtes ce pro ? Revendiquez
            </Link>
          </div>
        </div>
      )}

      {/* Structured Data for SEO */}
      <LocalBusinessSchema pro={{
        companyName: pro.companyName,
        description: pro.description,
        city: pro.city,
        postalCode: pro.postalCode,
        addressLine1: pro.addressLine1,
        ratingAvg: pro.ratingAvg,
        reviewCount: totalReviews,
        slug: pro.slug,
        latitude: pro.latitude,
        longitude: pro.longitude,
        logoUrl: pro.logoUrl,
        coverImageUrl: pro.coverImageUrl,
        primaryCategorySlug: pro.categories[0]?.slug ?? null,
        primaryCategoryName: pro.categories[0]?.name ?? null,
        serviceAreaDepartments: serviceAreas.flatMap((sa) => sa.departmentCodes ?? []),
      }} />
      <ReviewSchema
        reviews={reviews}
        pro={{ companyName: pro.companyName, slug: pro.slug, ratingAvg: pro.ratingAvg, reviewCount: totalReviews }}
      />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Professionnels', url: 'https://www.afficheservices.fr/artisans' },
        ...(pro.categories[0] ? [{ name: pro.categories[0].name, url: `https://www.afficheservices.fr/services/${pro.categories[0].slug}` }] : []),
        { name: pro.companyName, url: `https://www.afficheservices.fr/pro/${pro.slug}` },
      ]} />

      {/* ── Cover image / colored banner ──────────────── */}
      <div className="relative h-40 bg-gradient-to-br from-primary via-blue-600 to-indigo-700 sm:h-52">
        {pro.coverImageUrl && (
          <img src={pro.coverImageUrl} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-40" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        {/* ── Profile header (overlaps cover) ─────────── */}
        <div className="-mt-16 relative z-10 rounded-2xl border border-border bg-white p-6 shadow-md sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
            {/* Avatar */}
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl border-4 border-white bg-primary/10 text-3xl font-bold text-primary shadow-md sm:-mt-16 sm:h-28 sm:w-28">
              {pro.logoUrl ? (
                <img src={pro.logoUrl} alt={pro.companyName} className="h-full w-full rounded-2xl object-cover" />
              ) : (
                pro.companyName[0]
              )}
            </div>

            <div className="flex-1">
              {/* Name + badges */}
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{pro.companyName}</h1>
                {pro.identityVerified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-primary">
                    <BadgeCheck className="h-3.5 w-3.5" /> Vérifié
                  </span>
                )}
                {pro.insuranceVerified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
                    <Shield className="h-3.5 w-3.5" /> Assuré
                  </span>
                )}
                {pro.isDemo && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                    <Building2 className="h-3.5 w-3.5" /> Annuaire SIRENE
                  </span>
                )}
                {!pro.isDemo && pro.subscriptionTier !== 'free' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                    <Sparkles className="h-3.5 w-3.5" /> Pro
                  </span>
                )}
              </div>

              {/* Location */}
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {pro.city} ({pro.departmentCode})
                </span>
                {pro.ownerName && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" /> {pro.ownerName}
                  </span>
                )}
                {pro.website && (
                  <a
                    href={pro.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <Globe className="h-3.5 w-3.5" /> Site web
                  </a>
                )}
              </div>

              {/* Stats row — social proof */}
              <div className="mt-4 flex flex-wrap gap-3">
                {totalReviews > 0 ? (
                  <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                      <span className="text-lg font-bold text-slate-900">{formatRating(pro.ratingAvg)}</span>
                    </div>
                    <span className="text-xs text-slate-500">({totalReviews} avis)</span>
                  </div>
                ) : (
                  <div className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-medium text-blue-600">
                    Nouveau sur la plateforme
                  </div>
                )}
                {responseHours > 0 && (
                  <div className="flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-sm">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="font-medium text-slate-700">Répond en ~{formatHours(pro.responseTimeAvgHours)}</span>
                  </div>
                )}
                {pro.yearsExperience && (
                  <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm">
                    <Briefcase className="h-4 w-4 text-slate-500" />
                    <span className="font-medium text-slate-700">{pro.yearsExperience} ans d'expérience</span>
                  </div>
                )}
                {pro.employeeCount > 1 && (
                  <div className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm">
                    <UsersIcon className="h-4 w-4 text-slate-500" />
                    <span className="font-medium text-slate-700">{pro.employeeCount} personnes</span>
                  </div>
                )}
                {acceptanceRate > 0 && (
                  <div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-sm">
                    <ThumbsUp className="h-4 w-4 text-emerald-500" />
                    <span className="font-medium text-slate-700">{acceptanceRate}% acceptation</span>
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className="mt-3 flex flex-wrap gap-2">
                {pro.categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/services/${cat.slug}`}
                    className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-primary/10 hover:text-primary"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Breadcrumb ──────────────────────────────── */}
        <nav className="mt-4 flex items-center gap-2 text-xs text-slate-500">
          <Link to="/" className="hover:text-primary">Accueil</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-primary">Services</Link>
          {pro.categories[0] && (
            <>
              <span>/</span>
              <Link to={`/services/${pro.categories[0].slug}`} className="hover:text-primary">
                {pro.categories[0].name}
              </Link>
            </>
          )}
          <span>/</span>
          <span className="text-slate-900">{pro.companyName}</span>
        </nav>

        {/* ── Live activity ticker — urgency without fake ── */}
        {weeklyLeadCount > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800 sm:text-sm">
            <span className="flex items-center gap-2">
              <span aria-hidden>🔥</span>
              <span>
                <strong className="font-semibold">{weeklyLeadCount}</strong> devis demandés pour {pro.categories[0]?.name?.toLowerCase() ?? 'ce service'} à {pro.city} cette semaine
              </span>
            </span>
            <Link
              to={`/demande-devis/1${pro.categories?.[0]?.id ? `?cat=${pro.categories[0].id}` : ''}`}
              className="hidden shrink-0 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 sm:inline-flex"
            >
              Mon devis →
            </Link>
          </div>
        )}

        {/* ── Main grid ───────────────────────────────── */}
        <div className="mt-6 grid gap-6 pb-12 lg:grid-cols-3">
          {/* Left column */}
          <div className="space-y-6 lg:col-span-2">
            {/* About */}
            {pro.description && (
              <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
                <h2 className="text-lg font-semibold text-slate-900">À propos</h2>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-600">
                  {pro.description}
                </p>
              </div>
            )}

            {/* Portfolio — hidden for directory-imported pros (no real photos) */}
            {!pro.isDemo && <PortfolioGallery images={portfolio} />}

            {/* Maintenance contracts */}
            {contracts.length > 0 && <ContractsBlock contracts={contracts} proCompany={pro.companyName} />}

            {/* Certifications & Verifications */}
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <FileCheck className="h-5 w-5 text-slate-400" />
                Vérifications et certifications
              </h2>

              {/* Verification badges */}
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[
                  { label: 'Identité vérifiée', ok: pro.identityVerified, icon: BadgeCheck, okCls: 'border-blue-200 bg-blue-50', iconCls: 'text-blue-600' },
                  { label: 'Assurance RC Pro', ok: pro.insuranceVerified, icon: Shield, okCls: 'border-emerald-200 bg-emerald-50', iconCls: 'text-emerald-600' },
                  { label: 'SIRET vérifié', ok: pro.siretVerified, icon: Building2, okCls: 'border-violet-200 bg-violet-50', iconCls: 'text-violet-600' },
                ].map((v) => (
                  <div
                    key={v.label}
                    className={`flex items-center gap-3 rounded-xl border p-4 ${
                      v.ok ? v.okCls : 'border-border bg-slate-50'
                    }`}
                  >
                    {v.ok ? (
                      <v.icon className={`h-5 w-5 ${v.iconCls}`} />
                    ) : (
                      <v.icon className="h-5 w-5 text-slate-300" />
                    )}
                    <div>
                      <span className={`text-sm font-medium ${v.ok ? 'text-slate-900' : 'text-slate-400'}`}>
                        {v.label}
                      </span>
                      <span className={`block text-[11px] ${v.ok ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {v.ok ? 'Confirmé' : 'Non vérifié'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Certifications list */}
              {certifications.length > 0 && (
                <div className="mt-5 border-t border-border pt-5">
                  <h3 className="text-sm font-semibold text-slate-700">Certifications professionnelles</h3>
                  <div className="mt-3 space-y-2">
                    {certifications.map((cert) => (
                      <div key={cert.id} className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3">
                        <Award className={`h-5 w-5 ${cert.verified ? 'text-amber-500' : 'text-slate-400'}`} />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-slate-900">{cert.name}</span>
                          {cert.issuer && (
                            <span className="ml-2 text-xs text-slate-400">par {cert.issuer}</span>
                          )}
                        </div>
                        {cert.verified && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                            Vérifié
                          </span>
                        )}
                        {cert.expiresAt && (
                          <span className="text-[10px] text-slate-400">
                            exp. {new Date(cert.expiresAt).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Service area */}
            {serviceAreas.length > 0 && (
              <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <MapPin className="h-5 w-5 text-slate-400" />
                  Zone d'intervention
                </h2>
                <div className="mt-4 space-y-3">
                  {serviceAreas.map((area) => (
                    <div key={area.id} className="rounded-xl bg-slate-50 px-4 py-3">
                      {area.type === 'radius' && (
                        <p className="text-sm text-slate-700">
                          <strong>Rayon de {area.radiusKm} km</strong> autour de {pro.city}
                        </p>
                      )}
                      {area.type === 'departments' && area.departmentCodes && (
                        <div>
                          <p className="text-sm font-medium text-slate-700">Départements couverts :</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {area.departmentCodes.map((code) => (
                              <span key={code} className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-xs">
                                {code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {area.type === 'communes' && area.communeCodes && (
                        <p className="text-sm text-slate-700">
                          <strong>{area.communeCodes.length} communes</strong> desservies
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lead-gen quiz — shown on all pro pages to convert traffic
                into devis requests. On real pros with reviews, it complements
                the reviews block. On demo/claim pros with 0 reviews, it IS
                the primary conversion tool. */}
            {pro.categories[0] && (
              <InlineQuiz
                serviceName={pro.categories[0].name.toLowerCase()}
                cityName={pro.city}
                categorySlug={pro.categories[0].slug}
              />
            )}

            {/* ── Similar artisans — "Compare 3 devis" positioning ── */}
            {similarPros.length > 0 && pro.categories[0] && (
              <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      Comparez {similarPros.length + 1} {pro.categories[0].name.toLowerCase()}s à {pro.city}
                    </h2>
                    <p className="mt-1 text-xs text-slate-500">
                      Recevez 3 devis gratuits et choisissez le meilleur rapport qualité/prix.
                    </p>
                  </div>
                  <Link
                    to={`/demande-devis/1?cat=${pro.categories[0].id}`}
                    className="hidden shrink-0 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-primary-hover sm:inline-flex"
                  >
                    3 devis gratuits
                  </Link>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {similarPros.map((sp) => (
                    <div key={sp.id} className="rounded-xl border border-border bg-slate-50 p-4">
                      <p className="truncate text-sm font-semibold text-slate-900">{sp.companyName}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{sp.city}</p>
                      {Number(sp.reviewCount) > 0 && sp.ratingAvg ? (
                        <div className="mt-2 flex items-center gap-1 text-xs text-slate-600">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="font-semibold">{formatRating(sp.ratingAvg)}</span>
                          <span className="text-slate-400">({sp.reviewCount} avis)</span>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle className="h-3 w-3" /> SIRET vérifié
                        </div>
                      )}
                      <Link
                        to={`/pro/${sp.slug}`}
                        className="mt-3 block w-full rounded-lg border border-border bg-white py-1.5 text-center text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary"
                      >
                        Voir le profil
                      </Link>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/demande-devis/1?cat=${pro.categories[0].id}`}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover sm:hidden"
                >
                  Recevoir 3 devis gratuits <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            {/* ── Service-specific FAQ — objection handling + SEO ── */}
            {pro.categories[0] && (() => {
              const svcName = pro.categories[0].name.toLowerCase()
              const catId = pro.categories[0].id
              const faqs = [
                { question: `Combien coûte ${svcName} à ${pro.city} ?`, answer: `Le tarif moyen pour ${svcName} à ${pro.city} varie selon la complexité du projet. Pour obtenir un chiffrage précis, demandez un devis gratuit — vous recevrez jusqu'à 3 propositions comparatives sous 48h.` },
                { question: `Quel est le délai moyen d'intervention ?`, answer: `En moyenne, les artisans vérifiés de ${pro.city} répondent sous 24-48h. Pour une urgence, utilisez le service urgence 24h qui garantit un rappel sous 2h.` },
                { question: `${pro.companyName} propose-t-il des garanties ?`, answer: `Les artisans référencés sur AfficheServices sont vérifiés (SIRET + assurance responsabilité civile). Pour les travaux de gros œuvre et rénovation, la garantie décennale (10 ans) est obligatoire. Demandez l'attestation avant signature.` },
                { question: `Comment comparer plusieurs devis ?`, answer: `Remplissez notre formulaire en 2 minutes : décrivez votre projet, votre localisation et votre urgence. Vous recevrez jusqu'à 3 devis d'artisans vérifiés à ${pro.city}, gratuits et sans engagement.` },
                { question: `Les avis clients sont-ils vérifiés ?`, answer: `Oui. Seuls les clients ayant effectivement demandé un devis via AfficheServices peuvent laisser un avis. Chaque avis est modéré avant publication.` },
              ]
              return (
                <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
                  <FAQSchema items={faqs} />
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                    <MessageSquare className="h-5 w-5 text-slate-400" />
                    Questions fréquentes
                  </h2>
                  <div className="mt-4 space-y-2">
                    {faqs.map((item, i) => (
                      <details key={i} className="group rounded-xl border border-border bg-slate-50 p-4">
                        <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-semibold text-slate-900">
                          {item.question}
                          <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
                        </summary>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.answer}</p>
                        <Link
                          to={`/demande-devis/1?cat=${catId}`}
                          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          Demander un devis précis <ArrowRight className="h-3 w-3" />
                        </Link>
                      </details>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* Reviews section — shown only when there are approved reviews.
                Hidden entirely otherwise (no "Aucun avis" empty state, no
                claim placeholder — the claim banner at the top + quiz above
                handle those cases). */}
            {totalReviews > 0 && (
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <MessageSquare className="h-5 w-5 text-slate-400" />
                  Avis clients ({totalReviews})
                </h2>
              </div>

              {/* Rating summary */}
              {totalReviews > 0 && ratingBreakdown && (
                <div className="mt-5 flex flex-col gap-6 rounded-xl bg-slate-50 p-5 sm:flex-row sm:items-start">
                  {/* Big score */}
                  <div className="shrink-0 text-center sm:text-left">
                    <p className="text-5xl font-bold text-slate-900">{pro.ratingAvg}</p>
                    <StarRating rating={ratingNum} size={18} />
                    <p className="mt-1 text-xs text-slate-500">{totalReviews} avis</p>
                  </div>

                  {/* Distribution bars */}
                  <div className="flex-1 space-y-1.5">
                    <DistributionBar stars={5} count={Number(ratingBreakdown.count5)} total={totalReviews} />
                    <DistributionBar stars={4} count={Number(ratingBreakdown.count4)} total={totalReviews} />
                    <DistributionBar stars={3} count={Number(ratingBreakdown.count3)} total={totalReviews} />
                    <DistributionBar stars={2} count={Number(ratingBreakdown.count2)} total={totalReviews} />
                    <DistributionBar stars={1} count={Number(ratingBreakdown.count1)} total={totalReviews} />
                  </div>

                  {/* Category breakdown */}
                  <div className="w-full space-y-2 sm:w-56">
                    <RatingBar label="Qualité" value={parseFloat(ratingBreakdown.avgQuality ?? '0') || null} />
                    <RatingBar label="Ponctualité" value={parseFloat(ratingBreakdown.avgPunctuality ?? '0') || null} />
                    <RatingBar label="Communication" value={parseFloat(ratingBreakdown.avgCommunication ?? '0') || null} />
                    <RatingBar label="Rapport qualité/prix" value={parseFloat(ratingBreakdown.avgValue ?? '0') || null} />
                  </div>
                </div>
              )}

              {/* Review filter tabs */}
              {totalReviews > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setReviewFilter(null)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                      reviewFilter === null ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Tous ({totalReviews})
                  </button>
                  {[5, 4, 3, 2, 1].map((stars) => {
                    const count = Number(ratingBreakdown?.[`count${stars}` as keyof typeof ratingBreakdown] ?? 0)
                    if (count === 0) return null
                    return (
                      <button
                        key={stars}
                        type="button"
                        onClick={() => setReviewFilter(reviewFilter === stars ? null : stars)}
                        className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                          reviewFilter === stars ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {stars} <Star className={`h-3 w-3 ${reviewFilter === stars ? 'fill-white text-white' : 'fill-amber-400 text-amber-400'}`} /> ({count})
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Review list */}
              <div className="mt-6 divide-y divide-border">
                {reviews.filter((rev) => reviewFilter === null || rev.overallRating === reviewFilter).map((rev) => (
                  <div key={rev.id} className="py-5 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-500">
                          {rev.reviewerFirstName[0]}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">
                              {rev.reviewerFirstName} {rev.reviewerLastName?.[0]}.
                            </span>
                            {rev.isVerifiedPurchase && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                                <CheckCircle className="h-2.5 w-2.5" /> Achat vérifié
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2">
                            <StarRating rating={rev.overallRating} size={12} />
                            <span className="text-xs text-slate-400">
                              {new Date(rev.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {rev.title && (
                      <p className="mt-2 text-sm font-semibold text-slate-800">{rev.title}</p>
                    )}
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{rev.comment}</p>

                    {/* Review photos */}
                    {rev.photos.length > 0 && (
                      <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-hide">
                        {rev.photos.map((photo) => (
                          <div
                            key={photo.id}
                            className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-slate-100"
                          >
                            <img
                              src={photo.imageUrl}
                              alt={photo.caption ?? 'Photo du projet'}
                              className="h-full w-full object-cover transition-transform group-hover:scale-110"
                              loading="lazy"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Sub-ratings */}
                    {(rev.qualityRating || rev.punctualityRating || rev.communicationRating || rev.valueRating) && (
                      <div className="mt-3 flex flex-wrap gap-3">
                        {rev.qualityRating && (
                          <span className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                            Qualité: {rev.qualityRating}/5
                          </span>
                        )}
                        {rev.punctualityRating && (
                          <span className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                            Ponctualité: {rev.punctualityRating}/5
                          </span>
                        )}
                        {rev.communicationRating && (
                          <span className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                            Communication: {rev.communicationRating}/5
                          </span>
                        )}
                        {rev.valueRating && (
                          <span className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
                            Rapport Q/P: {rev.valueRating}/5
                          </span>
                        )}
                      </div>
                    )}

                    {/* Pro response */}
                    {rev.response && (
                      <div className="mt-3 ml-6 rounded-xl border-l-2 border-primary/30 bg-blue-50/50 p-4">
                        <p className="text-xs font-semibold text-primary">Réponse de {pro.companyName}</p>
                        <p className="mt-1 text-sm text-slate-600">{rev.response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>

          {/* ── Right sidebar ─────────────────────────── */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-4">
              {/* CTA Card */}
              <div className="rounded-2xl border border-border bg-white p-6 shadow-md">
                {pro.isDemo ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-blue-500" />
                      <h3 className="text-lg font-semibold text-slate-900">Fiche annuaire</h3>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      Données officielles du registre SIRENE (INSEE).
                    </p>
                    <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                      <p className="text-sm font-semibold text-blue-900">Vous êtes {pro.companyName} ?</p>
                      <p className="mt-1 text-xs text-blue-700">Revendiquez votre profil pour recevoir des demandes de devis gratuits.</p>
                      <Link
                        to={`/revendiquer?siret=${pro.siret}`}
                        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
                      >
                        Revendiquer ce profil
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                    <div className="mt-4 space-y-2 text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> SIRET vérifié ✓
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> Entreprise active
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-slate-400" /> Coordonnées sur demande
                      </div>
                    </div>
                    <p className="mt-4 rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600">
                      <strong>Important :</strong> AfficheServices ne communique pas les numéros
                      des entreprises figurant sur les fiches annuaire. Pour être mis en relation,
                      utilisez le formulaire de devis ci-dessus — nous transmettons votre demande
                      aux artisans disponibles dans votre département.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-slate-900">Demander un devis</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Réponse {responseHours > 0 ? `en ~${responseHours}h` : 'sous 48h'}
                    </p>
                    <Link
                      to={`/demande-devis/1${pro.categories?.[0]?.id ? `?cat=${pro.categories[0].id}` : ''}`}
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
                    >
                      Demander un devis gratuit
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    <p className="mt-3 text-[11px] text-center text-slate-500">
                      Réponse directe du pro après envoi de votre demande.
                    </p>
                  </>
                )}
                <div className="mt-4 space-y-2.5 text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> 100% gratuit, sans engagement
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> Jusqu'à 3 devis comparatifs
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> Professionnel vérifié et assuré
                  </div>
                </div>
                <div className="mt-4 flex justify-center">
                  <GuaranteeBadge />
                </div>

                {/* Share buttons */}
                <div className="mt-4 border-t border-border pt-4">
                  <p className="text-xs text-slate-400">Partager ce profil</p>
                  <div className="mt-2 flex gap-2">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`${pro.companyName} — Artisan vérifié sur AfficheServices\nhttps://www.afficheservices.fr/pro/${pro.slug}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 transition-colors hover:bg-emerald-100"
                      aria-label="Partager sur WhatsApp"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.afficheservices.fr/pro/${pro.slug}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition-colors hover:bg-blue-100"
                      aria-label="Partager sur Facebook"
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </a>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard?.writeText(`https://www.afficheservices.fr/pro/${pro.slug}`); }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-slate-500 transition-colors hover:bg-slate-100"
                      aria-label="Copier le lien"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats card */}
              <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Statistiques</h4>
                <div className="mt-3 space-y-3">
                  {[
                    { label: 'Note moyenne', value: `${pro.ratingAvg ?? '—'}/5`, icon: Star },
                    { label: 'Avis clients', value: String(totalReviews), icon: MessageSquare },
                    { label: "Taux d'acceptation", value: `${acceptanceRate || '—'}%`, icon: ThumbsUp },
                    { label: 'Temps de réponse', value: `${responseHours || '—'}h`, icon: Clock },
                    { label: 'Expérience', value: `${pro.yearsExperience ?? '—'} ans`, icon: Briefcase },
                    { label: 'Équipe', value: `${pro.employeeCount} personne${pro.employeeCount > 1 ? 's' : ''}`, icon: UsersIcon },
                  ].map((stat) => (
                    <div key={stat.label} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-slate-500">
                        <stat.icon className="h-3.5 w-3.5" /> {stat.label}
                      </span>
                      <span className="font-medium text-slate-900">{stat.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info card */}
              <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Informations</h4>
                <div className="mt-3 space-y-2.5 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Building2 className="h-3.5 w-3.5 text-slate-400" />
                    SIRET : {pro.siret.replace(/(.{3})(.{3})(.{3})(.{5})/, '$1 $2 $3 $4')}
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                    {pro.city}, {pro.postalCode}
                  </div>
                  {pro.website && (
                    <a
                      href={pro.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-primary hover:underline"
                    >
                      <Globe className="h-3.5 w-3.5" /> Visiter le site web
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    Membre depuis {new Date(pro.createdAt).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hub-spoke reverse links — connect indexable pro back to category &
          city silos. Only renders for pros that have a primary category and a
          city slug; otherwise pages stay clean. */}
      {pro.categories[0] && (() => {
        const catSlug = pro.categories[0].slug
        const artisan = artisanSlug(catSlug)
        const artisanLabel = SERVICE_LABELS[catSlug]?.artisan ?? catSlug
        const cSlug = pro.city
          ? pro.city.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
          : null
        if (!cSlug) return null
        return (
          <section className="border-t border-border bg-surface-secondary py-10">
            <div className="mx-auto max-w-7xl px-4 lg:px-6">
              <h2 className="text-base font-bold text-slate-900">Pages liées</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Link to={`/services/${catSlug}`} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
                  <p className="text-xs font-semibold text-slate-500">Catégorie</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{pro.categories[0].name}</p>
                </Link>
                <Link to={`/prix/${artisan}-${cSlug}`} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
                  <p className="text-xs font-semibold text-slate-500">Tarifs locaux</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">Prix {artisanLabel} {pro.city}</p>
                </Link>
                <Link to={`/travaux/${cSlug}`} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
                  <p className="text-xs font-semibold text-slate-500">Hub local</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">Travaux à {pro.city}</p>
                </Link>
                <Link to={`/tarif/${artisan}`} className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
                  <p className="text-xs font-semibold text-slate-500">Référence</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">Tarifs {artisanLabel} France</p>
                </Link>
              </div>
            </div>
          </section>
        )
      })()}

      <Footer />

      {/* Sticky mobile CTA — positioned above bottom nav (bottom-16) */}
      <div className="fixed inset-x-0 bottom-16 z-40 border-t border-border bg-white/95 p-3 backdrop-blur-sm lg:hidden lg:bottom-0">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{pro.companyName}</p>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              {pro.isDemo ? (
                <span className="text-amber-600">Profil de démonstration</span>
              ) : (
                <>
                  {Number(pro.ratingAvg) > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {pro.ratingAvg}
                    </span>
                  )}
                  <span>· Répond {responseHours > 0 ? `en ~${responseHours}h` : 'sous 48h'}</span>
                </>
              )}
            </div>
          </div>
          <Link
            to={pro.isDemo ? `/revendiquer?siret=${pro.siret}` : `/demande-devis/1${pro.categories?.[0]?.id ? `?cat=${pro.categories[0].id}` : ''}`}
            className="shrink-0 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            {pro.isDemo ? 'Revendiquer' : 'Devis gratuit'}
          </Link>
        </div>
      </div>
      {/* Bottom padding to prevent content from being hidden behind sticky CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  )
}
